import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToCleaningHostReservations } from "../../../../redux/cleaningReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

/* ── green tokens ── */
const G = {
  green:      "#16a34a",
  greenDark:  "#14532d",
  greenLight: "#dcfce7",
  border:     "#bbf7d0",
  border2:    "#86efac",
  muted:      "#6b7280",
  text:       "#14532d",
  surface:    "#ffffff",
  bg:         "#f0fdf4",
};

const CleaningReservations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user       = useSelector((s: RootState) => s.auth.user);
  const userId     = user?.userId || (user as any)?.uid || null;
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);

  const reservations = useSelector((s: RootState) => s.cleaningReservations.hostReservations);
  const allUsers     = useSelector((s: RootState) => s.auth.allUsers);
  const listings     = useSelector((s: RootState) => s.cleaning.listings);

  const [expanded,        setExpanded]        = useState<string | null>(null);
  const [fetchedListings, setFetchedListings] = useState<string[]>([]);

  useEffect(() => {
    if (authLoaded && userId) {
      dispatch(listenToCleaningHostReservations(userId));
      dispatch(fetchAllUsers());
    }
  }, [authLoaded, userId, dispatch]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToCleaningData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
  }, [reservations, fetchedListings, dispatch]);

  const getGuest = (guestId: string) => allUsers.find((u) => u.userId === guestId);

  const getListingPhoto = (listingId: string) => {
    const l = listings.find((l) => l.listingId === listingId);
    return l?.photos?.[0] || "/fallback.jpg";
  };

  // ── Shipping address from the booker's profile ──
  const getGuestShippingAddress = (guest: any) => {
    if (!guest) return null;
    const a = guest.shippingAddress || {};
    const parts = [
      a.streetAddress,
      a.apartmentNumber,
      a.postalAddress,
      a.city,
      a.state,
      a.country,
      a.zipCode,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  };

  // ── Contact number: workPhoneNumber or whatsappNumber ──
  const getGuestContact = (guest: any) => {
    if (!guest) return null;
    return guest.workPhoneNumber || guest.whatsappNumber || null;
  };

  const getActiveFlagLabel = (r: any) => {
    if (r.isCheckingIn)   return "Service Starting";
    if (r.isOngoing)      return "Service Ongoing";
    if (r.isCheckingOut)  return "Service Ending";
    if (r.isArrivingSoon) return "Arriving Soon";
    if (r.isUpcoming)     return "Upcoming";
    if (r.isHosted)       return "Completed";
    return "Active";
  };

  const activeReservations = reservations.filter(
    (r) => !r.isCancelled && !r.isCompleted && !r.isCheckingOut
  );

  return (
    <div style={s.gridContainer}>
      {activeReservations.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: G.muted }}>
          <span style={{ fontSize: 44 }}>🧹</span>
          <p style={{ marginTop: 12, fontWeight: 600, color: G.text }}>No active reservations</p>
        </div>
      ) : (
        activeReservations.map((res) => {
          const guest        = getGuest(res.guestId);
          const listingPhoto = getListingPhoto(res.listingId);
          const flag         = getActiveFlagLabel(res);
          const initials     = guest?.firstName?.[0]?.toUpperCase() || "C";
          const disableCancel = res.hasCheckedIn || res.isOngoing || res.isCheckingOut;
          const isExpanded   = expanded === res.id;

          const guestPath       = `/aboutme/@${guest?.username || res.guestId}`;
          const shippingAddress = getGuestShippingAddress(guest);
          const contactNumber   = getGuestContact(guest);

          return (
            <div key={res.id} style={s.card}>

              {/* ── header row ── */}
              <div style={s.cardHeader} onClick={() => setExpanded(isExpanded ? null : res.id)}>
                <img src={listingPhoto} style={s.thumbnail} alt="listing" />

                <div style={s.headerInfo}>
                  <div style={s.guestSection}>
                    {guest?.profilePicture ? (
                      <img
                        src={guest.profilePicture}
                        style={s.avatar}
                        alt="guest"
                        onClick={(e) => { e.stopPropagation(); navigate(guestPath); }}
                      />
                    ) : (
                      <div
                        style={s.initialsCircle}
                        onClick={(e) => { e.stopPropagation(); navigate(guestPath); }}
                      >
                        {initials}
                      </div>
                    )}
                    <div>
                      <p style={s.guestName}>{guest?.firstName || "Client"} {guest?.lastName || ""}</p>
                      <span style={{
                        ...s.flagBadge,
                        background: res.isOngoing ? G.greenLight : "#fef9c3",
                        color:      res.isOngoing ? G.greenDark  : "#92400e",
                        border:     `1px solid ${res.isOngoing ? G.border2 : "#fde68a"}`,
                      }}>
                        {flag}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={s.chevron}>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* ── expanded details ── */}
              {isExpanded && (
                <div style={s.expand}>
                  <div style={s.detailGrid}>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Service Date</span>
                      <span style={s.detailVal}>{new Date(res.checkIn).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>End Date</span>
                      <span style={s.detailVal}>{new Date(res.checkOut).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Total Paid</span>
                      <span style={{ ...s.detailVal, color: G.green, fontWeight: 700 }}>€{(res.totalPaid || 0).toFixed(2)}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Your Profit</span>
                      <span style={{ ...s.detailVal, color: G.green, fontWeight: 700 }}>€{(res.hostProfit || 0).toFixed(2)}</span>
                    </div>
                    <div style={s.detailItem}>
                      <span style={s.detailLabel}>Service Fee</span>
                      <span style={s.detailVal}>€{(res.hostsFee || 0).toFixed(2)}</span>
                    </div>

                  </div>

                  {/* ── Service Address — labeled vertically ── */}
                  <div style={s.addressBlock}>
                    <span style={{ ...s.detailLabel, marginBottom: 6, display: "block" }}>📍 Service Address</span>
                    {(() => {
                      const a = guest?.shippingAddress || {};
                      const fields = [
                        { label: "Street",    val: a.streetAddress    },
                        { label: "Apt/Suite", val: a.apartmentNumber  },
                        { label: "P.O. Box",  val: a.postalAddress    },
                        { label: "City",      val: a.city             },
                        { label: "State",     val: a.state            },
                        { label: "Country",   val: a.country          },
                        { label: "Zip Code",  val: a.zipCode          },
                      ].filter(f => f.val);

                      return fields.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {fields.map(({ label, val }) => (
                            <div key={label} style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: G.muted, letterSpacing: "0.08em", minWidth: 56, textTransform: "uppercase" }}>{label}</span>
                              <span style={{ fontSize: 13, color: G.text, fontWeight: 500 }}>{val}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: G.muted, fontStyle: "italic", fontSize: 11 }}>No address on file</span>
                      );
                    })()}
                  </div>

                  {/* ── Contact + Message row ── */}
                  <div style={s.contactRow}>
                    <div style={s.contactLeft}>
                      <span style={s.detailLabel}>Contact</span>
                      {contactNumber ? (
                        /* phone icon — tap to call */
                        <a
                          href={`tel:${contactNumber}`}
                          style={s.phoneLink}
                          onClick={e => e.stopPropagation()}
                          title={contactNumber}
                        >
                          📞
                        </a>
                      ) : (
                        <span style={s.noContactText}>
                          Booker has not placed a contact request via app messages
                        </span>
                      )}
                    </div>

                    {/* Message → navigate to IMessages thread */}
                    <button
                      style={s.messageBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        // threadId is always smallerUid_largerUid sorted
                        const ids = [userId, res.guestId].sort();
                        const threadId = `${ids[0]}_${ids[1]}`;
                        navigate(`/imessages/${threadId}`);
                      }}
                    >
                      💬 Message
                    </button>
                  </div>

                  {/* checked-in indicator */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "10px 0" }}>
                    {res.hasCheckedIn
                      ? <CheckCircleIcon style={{ color: G.green, fontSize: 17 }} />
                      : <CancelIcon     style={{ color: "#dc2626", fontSize: 17 }} />
                    }
                    <span style={{ fontSize: 12, color: G.muted }}>
                      {res.hasCheckedIn ? "Service started" : "Not yet started"}
                    </span>
                  </div>

                  <button
                    style={s.viewProfileBtn}
                    onClick={(e) => { e.stopPropagation(); navigate(guestPath); }}
                  >
                    View {guest?.firstName || "client"}'s profile
                  </button>

                  <button
                    disabled={disableCancel}
                    style={{
                      ...s.cancelBtn,
                      ...(disableCancel
                        ? { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed", boxShadow: "none" }
                        : {}),
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disableCancel) navigate(`/hostcancelcleaningbooking/${userId}/${res.listingId}/${res.id}`);
                    }}
                  >
                    Cancel Booking
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default CleaningReservations;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
    padding: 10,
    alignItems: "start",
  },
  card: {
    backgroundColor: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 16,
    boxShadow: "0 4px 16px rgba(22,163,74,.08)",
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    cursor: "pointer",
  },
  thumbnail: {
    width: 80,
    height: 80,
    borderRadius: 12,
    objectFit: "cover" as const,
    marginRight: 14,
    border: `2px solid ${G.border}`,
    flexShrink: 0,
  },
  headerInfo:   { flex: 1, display: "flex" },
  guestSection: { display: "flex", alignItems: "center", gap: 10 },
  avatar:         { width: 44, height: 44, borderRadius: "50%", objectFit: "cover" as const, cursor: "pointer", border: `2px solid ${G.green}`, flexShrink: 0 },
  initialsCircle: { width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: 700, fontSize: 16, cursor: "pointer", flexShrink: 0 },
  guestName:  { fontWeight: 600, fontSize: 14, color: G.text, marginBottom: 4 },
  flagBadge:  { fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, display: "inline-block" },
  chevron:    { marginLeft: 12, color: G.muted, display: "flex", alignItems: "center" },

  expand: {
    padding: "12px 18px 16px",
    borderTop: `1px solid ${G.border}`,
    backgroundColor: G.bg,
  },
  detailGrid:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", marginBottom: 10 },
  detailItem:  { display: "flex", flexDirection: "column" as const, gap: 2 },
  detailLabel: { fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: G.muted, textTransform: "uppercase" as const },
  detailVal:   { fontSize: 13, fontWeight: 500, color: G.text },

  addressBlock: {
    background: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 10,
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    background: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 10,
  },
  contactLeft: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 3,
    flex: 1,
  },
  noContactText: {
    fontSize: 11,
    color: G.muted,
    fontStyle: "italic" as const,
    lineHeight: 1.4,
  },
  messageBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "7px 14px",
    borderRadius: 999,
    background: G.greenDark,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    border: "none",
    flexShrink: 0,
    whiteSpace: "nowrap" as const,
    cursor: "pointer",
  },
  messageBtnDisabled: {
    background: "#e5e7eb",
    color: "#9ca3af",
    cursor: "not-allowed" as const,
    pointerEvents: "none" as const,
  },
  phoneLink: {
    fontSize: 22,
    textDecoration: "none",
    lineHeight: 1,
    cursor: "pointer",
  },

  viewProfileBtn: {
    marginTop: 8, marginBottom: 6, padding: "8px 14px",
    fontSize: 12, fontWeight: 600, borderRadius: 8,
    border: `1px solid ${G.border}`, background: G.surface,
    color: G.text, cursor: "pointer", width: "100%",
  },
  cancelBtn: {
    padding: "9px 14px", fontSize: 12, fontWeight: 700, borderRadius: 8,
    border: "none", background: "#dc2626", color: "#fff",
    cursor: "pointer", boxShadow: "0 4px 10px rgba(220,38,38,.25)",
    transition: "all .2s", width: "fit-content",
  },
};